import React, { useState, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DateRangePickerProps {
  dateRange: { from: Date; to: Date };
  onDateRangeChange: (from: Date, to: Date) => void;
  /** When set, visibility is controlled by the parent (mobile filter entry). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the default trigger button (use with controlled open from another control). */
  hideTrigger?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateRange,
  onDateRangeChange,
  open,
  onOpenChange,
  hideTrigger = false,
}) => {
  const [internalShow, setInternalShow] = useState(false);
  const isControlled = open !== undefined;
  const showDatePicker = isControlled ? open : internalShow;

  const setPickerOpen = (next: boolean) => {
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setInternalShow(next);
    }
  };

  const [tempDateRange, setTempDateRange] = useState(dateRange);

  // Sync tempDateRange when dateRange prop changes
  useEffect(() => {
    setTempDateRange(dateRange);
  }, [dateRange]);

  const handleDateRangeApply = () => {
    // Validate date range constraints
    const today = new Date();
    const oneYearAgo = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000);
    
    let validatedRange = { ...tempDateRange };
    
    // Ensure end date is not in the future
    if (validatedRange.to > today) {
      validatedRange.to = today;
    }
    
    // Ensure start date is not more than 1 year ago
    if (validatedRange.from < oneYearAgo) {
      validatedRange.from = oneYearAgo;
    }
    
    // Ensure start date is before end date
    if (validatedRange.from >= validatedRange.to) {
      validatedRange.from = new Date(validatedRange.to.getTime() - 24 * 60 * 60 * 1000);
    }
    
    setTempDateRange(validatedRange);
    onDateRangeChange(validatedRange.from, validatedRange.to);
    setPickerOpen(false);
  };

  const formatDateRange = () => {
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    };
    return `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`;
  };

  return (
    <div className="relative">
      {!hideTrigger && (
        <button
          type="button"
          onClick={() => setPickerOpen(!showDatePicker)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          <Calendar size={16} />
          <span className="text-sm-nova">{formatDateRange()}</span>
          <ChevronDown size={16} />
        </button>
      )}

      {showDatePicker && (
        <div
          className={
            hideTrigger
              ? 'z-50 bg-white rounded-lg shadow-lg p-4 border w-full max-w-md'
              : 'absolute top-12 right-0 z-50 bg-white rounded-lg shadow-lg p-4 border'
          }
        >
          <div className="flex gap-4 mb-4">
            <div>
              <label className="block text-sm-nova font-medium text-gray-700 mb-1 uppercase">From</label>
              <DatePicker
                selected={tempDateRange.from}
                onChange={(date) => setTempDateRange({ ...tempDateRange, from: date || new Date() })}
                className="w-32 p-2 border rounded text-black text-sm"
                dateFormat="MMM d, yyyy"
                maxDate={new Date()}
                minDate={new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)}
              />
            </div>
            <div>
              <label className="block text-sm-nova font-medium text-gray-700 mb-1 uppercase">To</label>
              <DatePicker
                selected={tempDateRange.to}
                onChange={(date) => setTempDateRange({ ...tempDateRange, to: date || new Date() })}
                className="w-32 p-2 border rounded text-black text-sm"
                dateFormat="MMM d, yyyy"
                maxDate={new Date()}
                minDate={tempDateRange.from}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="px-3 py-1 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDateRangeApply}
              className="px-3 py-1 bg-black-600 text-white rounded hover:bg-black-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;

